// Notification entity for glass notification toast system
import type { EntityConfig } from '@/hooks/useEntity';

export type NotificationType = 'message' | 'warning' | 'suggestion_response' | 'alert' | 'system';

export interface NotificationData {
  id?: number;
  userId: string; // User who receives this notification
  type: NotificationType;
  title: string;
  message: string;
  fromUserId?: string; // Admin/sender user ID (for messages/warnings)
  fromName?: string; // Display name of sender
  relatedId?: string; // Related entity ID (e.g., suggestion_id, message_id)
  actionUrl?: string; // Optional URL to navigate to on click
  read: boolean;
  acknowledged: boolean; // User clicked/dismissed
  priority: 'low' | 'normal' | 'high' | 'urgent';
  expiresAt?: string; // Auto-expire after this date
  createdAt?: string;
  updatedAt?: string;
}

export const notificationEntityConfig: EntityConfig<NotificationData> = {
  entityType: 'Notification',
  properties: [
    'userId',
    'type',
    'title',
    'message',
    'fromUserId',
    'fromName',
    'relatedId',
    'actionUrl',
    'read',
    'acknowledged',
    'priority',
    'expiresAt',
  ],
  requiredFields: ['userId', 'type', 'title', 'message', 'read', 'acknowledged', 'priority'],
  orderBy: {
    column: 'created_at',
    direction: 'DESC',
  },
};

// Notification color schemes by type
export const notificationColors = {
  message: {
    accent: 'bg-blue-500',
    bg: 'from-blue-500/20 to-blue-600/10',
    border: 'border-blue-400/30',
    icon: '💬',
  },
  warning: {
    accent: 'bg-red-500',
    bg: 'from-red-500/20 to-red-600/10',
    border: 'border-red-400/30',
    icon: '⚠️',
  },
  suggestion_response: {
    accent: 'bg-amber-500',
    bg: 'from-amber-500/20 to-amber-600/10',
    border: 'border-amber-400/30',
    icon: '💡',
  },
  alert: {
    accent: 'bg-purple-500',
    bg: 'from-purple-500/20 to-purple-600/10',
    border: 'border-purple-400/30',
    icon: '📢',
  },
  system: {
    accent: 'bg-slate-500',
    bg: 'from-slate-500/20 to-slate-600/10',
    border: 'border-slate-400/30',
    icon: 'ℹ️',
  },
};
